import type { FC, PropsWithChildren } from 'react'
import React, { useMemo } from 'react'

import styled from '@emotion/styled'

import type { Options as RichTextRenderOptions } from '@contentful/rich-text-react-renderer'
import { BLOCKS } from '@contentful/rich-text-types'

import theme from '@segment/matcha/foundations/Theme'

import SectionBase, * as Section from '@segment/matcha/recipes/Section'
import CopyTextBase from '@segment/matcha/recipes/CopyText'
import RichText, { ColoredTypography } from '@segment/matcha/recipes/RichText'

import type { Integration } from '@segment/api/staticSiteBackend/integration'

import LogoTitleBodyImage from './LogoTitleBodyImage'

interface LogoTitleBodyProps {
  integration: Integration
}

const options: RichTextRenderOptions = {
  renderNode: {
    [BLOCKS.HEADING_2]: (_, children) => <Heading variant="h2">{children}</Heading>,
    [BLOCKS.PARAGRAPH]: (_, children) => (
      <ColoredTypography variant="pLarge" tag="p" color={theme.palette.twilio.gray[100]}>
        {children}
      </ColoredTypography>
    ),
  },
}

const LogoTitleBody: FC<PropsWithChildren<LogoTitleBodyProps>> = ({ integration }) => {
  const withSegmentContent = useMemo(() => {
    if (!integration?.withSegment) {
      return
    }

    return <RichText data={integration?.withSegment} options={options} />
  }, [integration])

  return (
    <SectionBaseStyled variant="centered" copyVariant="wide">
      <SectionCopyStyled>
        {integration?.logo && (
          <CopyTextBase centered={true}>
            <LogoTitleBodyImage integration={integration} />
            <ContentWrapper>{withSegmentContent}</ContentWrapper>
          </CopyTextBase>
        )}
      </SectionCopyStyled>
    </SectionBaseStyled>
  )
}

const SectionCopyStyled = styled(Section.Copy)`
  max-width: 880px;
`

const Heading = styled(ColoredTypography)`
  text-align: center;

  ${({ theme }) => theme.breakpoints.up(`md`)} {
    margin-bottom: 70px;
  }
`

const ContentWrapper = styled.div`
  text-align: left;
`

const SectionBaseStyled = styled(SectionBase)`
  ${Section.Wrapper} {
    row-gap: 0;
  }
`

export default LogoTitleBody
